import type { NetWorthDomainScope } from '../components/NetWorthBreakdown';
import type {
  BankAccount,
  FinancialItem,
  InvestmentCase,
  InvestmentCompany,
  RealEstateProperty,
} from '../types';
import {
  formatEuro,
  formatMultiple,
  formatPercentPoints,
  formatSignedEuro,
} from '../utils/formatters';
import { computeCompanyMetrics } from '../utils/calculations';
import { investmentCaseAverageScore } from '../utils/investmentCase';
import {
  kpiDifference,
  kpiRatio,
  kpiSum,
  operandsFromItems,
  sumCalculation,
  type KpiExplain,
} from './kpiExplain';
import type { NetWorthSnapshot } from '../utils/netWorthSnapshot';

function netWorthDomainNote(domain: NetWorthDomainScope): string {
  if (domain === 'personal') return 'Personal domain: registry items tagged personal, plus real-estate equity.';
  if (domain === 'business') return 'Business domain: business registry items, PE portfolio, and bank cash.';
  return 'Combined view: all domains added together.';
}

export function buildHomeKpiExplains(
  snapshot: NetWorthSnapshot,
  domain: NetWorthDomainScope,
  breakdown?: {
    financialItems: FinancialItem[];
    companies: InvestmentCompany[];
    bankAccounts: BankAccount[];
    realEstateItems: RealEstateProperty[];
  },
): Record<string, KpiExplain> {
  const { netWorth, investments, assets, bankCash, realEstate, liabilities } = snapshot;
  const gross = assets + investments + bankCash + realEstate;
  const domainNote = netWorthDomainNote(domain);

  const portfolioExplain = breakdown
    ? kpiSum(
        'The paper value of your private-equity stakes — your share of each company’s valuation.',
        'Investments = Σ (company valuation × your ownership %)',
        companyEquityRows(breakdown.companies),
        formatEuro,
        'For each company we pick a valuation method (manual, revenue multiple, or EBITDA multiple), then multiply by your ownership percentage.',
        [domainNote, 'Counted toward business net worth in domain splits.'],
      )
    : {
        plainEnglish: 'The paper value of your private-equity stakes — your share of each company’s valuation.',
        formulaHeadline: 'Investments = Σ (company valuation × your ownership %)',
        formulaDetail:
          'For each company we pick a valuation method (manual, revenue multiple, or EBITDA multiple), then multiply by your ownership percentage.',
        operands: [{ symbol: 'Σ', label: 'Equity stakes summed', value: formatEuro(investments) }],
        calculation: formatEuro(investments),
        result: formatSignedEuro(investments),
        notes: [domainNote, 'Counted toward business net worth in domain splits.'],
      };

  const assetItems = breakdown
    ? breakdown.financialItems.filter((i) => i.type === 'asset')
    : [];
  const liabilityItems = breakdown
    ? breakdown.financialItems.filter((i) => i.type === 'liability')
    : [];
  const bankRows = breakdown
    ? breakdown.bankAccounts.map((a) => ({
        label: a.name,
        value: typeof a.balance === 'number' && Number.isFinite(a.balance) ? a.balance : 0,
      }))
    : [];
  const equityRows = breakdown
    ? breakdown.realEstateItems.map((p) => {
        const value =
          typeof p.currentValue === 'number' && Number.isFinite(p.currentValue) ? p.currentValue : 0;
        const mortgage =
          typeof p.mortgageBalance === 'number' && Number.isFinite(p.mortgageBalance)
            ? p.mortgageBalance
            : 0;
        return { label: p.name, value: value - mortgage };
      })
    : [];

  return {
    'current-total': {
      plainEnglish: 'Your net worth is everything you own minus everything you owe.',
      formulaHeadline: 'Net worth = Investments + Registry assets − Liabilities + Bank cash + Real-estate equity',
      formulaDetail:
        'We add up your equity stakes, tracked assets, liquid bank balances, and property equity, then subtract liabilities from the registry.',
      operands: [
        { symbol: 'Inv', label: 'Investments (your equity stakes)', value: formatEuro(investments) },
        { symbol: 'Reg', label: 'Registry assets', value: formatEuro(assets) },
        { symbol: 'Liab', label: 'Liabilities (subtracted)', value: formatEuro(liabilities) },
        { symbol: 'Bank', label: 'Bank cash', value: formatEuro(bankCash) },
        { symbol: 'RE', label: 'Real-estate equity', value: formatEuro(realEstate) },
      ],
      calculation: `${formatEuro(investments)} + ${formatEuro(assets)} − ${formatEuro(liabilities)} + ${formatEuro(bankCash)} + ${formatEuro(realEstate)}`,
      result: formatSignedEuro(netWorth),
      notes: [domainNote],
    },
    portfolio: portfolioExplain,
    registry: assetItems.length > 0
      ? kpiSum(
          'The sum of asset line items in your financial registry (excluding liabilities).',
          'Registry assets = Σ asset.currentValue',
          assetItems.map((i) => ({ label: i.name, value: i.currentValue })),
          formatEuro,
          'Only rows marked as type “asset” are included. Liabilities are tracked separately.',
          [domainNote],
        )
      : {
          plainEnglish: 'The sum of asset line items in your financial registry (excluding liabilities).',
          formulaHeadline: 'Registry assets = Σ asset.currentValue',
          formulaDetail: 'Only rows marked as type “asset” are included. Liabilities are tracked separately.',
          operands: [{ symbol: 'Σ', label: 'Asset line items', value: formatEuro(assets) }],
          calculation: formatEuro(assets),
          result: formatSignedEuro(assets),
          notes: [domainNote],
        },
    'bank-cash': bankRows.length > 0
      ? kpiSum(
          'Spendable EUR balances across connected and manual bank accounts.',
          'Bank cash = Σ account.balance (EUR only)',
          bankRows,
          formatEuro,
          'Non-EUR accounts are ignored in this total.',
          [domainNote],
        )
      : {
          plainEnglish: 'Spendable EUR balances across connected and manual bank accounts.',
          formulaHeadline: 'Bank cash = Σ account.balance (EUR only)',
          formulaDetail: 'Non-EUR accounts are ignored in this total.',
          operands: [{ symbol: 'Σ', label: 'EUR account balances', value: formatEuro(bankCash) }],
          calculation: formatEuro(bankCash),
          result: formatSignedEuro(bankCash),
          notes: [domainNote],
        },
    'real-estate': equityRows.length > 0
      ? kpiSum(
          'Property equity: what your buildings are worth minus outstanding mortgages.',
          'Real-estate equity = Σ (market value − mortgage balance)',
          equityRows,
          formatEuro,
          'Each property contributes its market value minus the remaining loan.',
          [domainNote, 'Real estate counts toward personal net worth in domain splits.'],
        )
      : {
          plainEnglish: 'Property equity: what your buildings are worth minus outstanding mortgages.',
          formulaHeadline: 'Real-estate equity = Σ (market value − mortgage balance)',
          formulaDetail: 'Each property contributes its market value minus the remaining loan.',
          operands: [{ symbol: 'Σ', label: 'Net equity across properties', value: formatEuro(realEstate) }],
          calculation: formatEuro(realEstate),
          result: formatSignedEuro(realEstate),
          notes: [domainNote, 'Real estate counts toward personal net worth in domain splits.'],
        },
    liabilities: liabilityItems.length > 0
      ? kpiSum(
          'Everything you owe — loans, mortgages tracked as registry liabilities, and similar.',
          'Liabilities = Σ liability.currentValue',
          liabilityItems.map((i) => ({ label: i.name, value: i.currentValue })),
          formatEuro,
          'Shown as a negative amount on the card because it reduces net worth.',
          [domainNote],
        )
      : {
          plainEnglish: 'Everything you owe — loans, mortgages tracked as registry liabilities, and similar.',
          formulaHeadline: 'Liabilities = Σ liability.currentValue',
          formulaDetail: 'Shown as a negative amount on the card because it reduces net worth.',
          operands: [{ symbol: 'Σ', label: 'Liability line items', value: formatEuro(liabilities) }],
          calculation: formatEuro(liabilities),
          result: `−${formatEuro(liabilities, true)}`,
          notes: [domainNote],
        },
    'debt-ratio': kpiRatio(
      'How much of your gross asset base is financed by debt — lower is usually safer.',
      'Debt ratio = Liabilities ÷ Gross assets × 100',
      { label: 'Liabilities', value: liabilities, formatted: formatEuro(liabilities) },
      { label: 'Gross assets (registry + investments + bank + RE)', value: gross, formatted: formatEuro(gross) },
      (v) => (gross <= 0 ? '—' : formatPercentPoints(v * 100)),
      'Gross assets include registry assets, investment marks, bank cash, and real-estate equity — liabilities are not subtracted from the denominator.',
      [domainNote],
    ),
  };
}

export function buildFinancialsKpiExplains(input: {
  items: FinancialItem[];
  summary: { assets: number; liabilities: number; net: number };
  assetCount: number;
  liabilityCount: number;
  topAsset: { name: string; share: number; value: number } | null;
  debtRatio: number | null;
  atRiskCount: number;
  domainFilter: 'all' | 'personal' | 'business';
  domainMix: { personal: number; business: number };
}): Record<string, KpiExplain> {
  const {
    items,
    summary,
    assetCount,
    liabilityCount,
    topAsset,
    debtRatio,
    atRiskCount,
    domainFilter,
    domainMix,
  } = input;

  const assetItems = items.filter((i) => i.type === 'asset');
  const liabilityItems = items.filter((i) => i.type === 'liability');

  return {
    net: kpiDifference(
      'Your balance-sheet headline: assets minus liabilities.',
      'Net position = Total assets − Total liabilities',
      { label: 'Total assets', value: summary.assets, formatted: formatEuro(summary.assets) },
      { label: 'Total liabilities', value: summary.liabilities, formatted: formatEuro(summary.liabilities) },
      formatEuro,
    ),
    assets: kpiSum(
      'Everything you count as an owned asset in the registry.',
      'Total assets = Σ asset.currentValue',
      assetItems.map((i) => ({ label: i.name, value: i.currentValue })),
      formatEuro,
      'Only line items with type “asset” are summed.',
    ),
    liabilities: kpiSum(
      'All outstanding obligations you track as liabilities.',
      'Total liabilities = Σ liability.currentValue',
      liabilityItems.map((i) => ({ label: i.name, value: i.currentValue })),
      formatEuro,
    ),
    items: {
      plainEnglish: 'How many asset vs. liability rows are in your filtered registry.',
      formulaHeadline: 'Registry items = asset count / liability count',
      operands: [
        { symbol: 'A', label: 'Assets', value: String(assetCount) },
        { symbol: 'L', label: 'Liabilities', value: String(liabilityCount) },
      ],
      calculation: `${assetCount} assets · ${liabilityCount} liabilities`,
      result: `${assetCount}/${liabilityCount}`,
    },
    monthly: kpiSum(
      'Combined monthly drift across every line item’s “monthly change” field.',
      'Monthly net change = Σ item.monthlyChange',
      items.map((i) => ({ label: i.name, value: i.monthlyChange })),
      formatSignedEuro,
      'Positive means the registry grew on paper this month; negative means it shrank.',
    ),
    'top-asset': topAsset
      ? kpiRatio(
          'How concentrated your registry is in the single largest asset.',
          'Largest asset share = Top asset value ÷ Total assets × 100',
          { label: topAsset.name, value: topAsset.value, formatted: formatEuro(topAsset.value) },
          { label: 'Total assets', value: summary.assets, formatted: formatEuro(summary.assets) },
          (v) => formatPercentPoints(v * 100),
        )
      : {
          plainEnglish: 'Share of total assets held in your biggest single asset.',
          formulaHeadline: 'Largest asset share = Top asset ÷ Total assets × 100',
          operands: [],
          calculation: '—',
          result: '—',
          notes: ['Add assets to see concentration.'],
        },
    'debt-ratio':
      debtRatio != null
        ? kpiRatio(
            'Registry leverage: liabilities as a percentage of assets on this view.',
            'Debt ratio = Total liabilities ÷ Total assets × 100',
            {
              label: 'Total liabilities',
              value: summary.liabilities,
              formatted: formatEuro(summary.liabilities),
            },
            { label: 'Total assets', value: summary.assets, formatted: formatEuro(summary.assets) },
            (v) => formatPercentPoints(v * 100),
            'Uses registry assets only — unlike the home debt ratio which includes investments and bank cash.',
          )
        : {
            plainEnglish: 'Registry leverage: liabilities as a percentage of assets.',
            formulaHeadline: 'Debt ratio = Total liabilities ÷ Total assets × 100',
            operands: [],
            calculation: '—',
            result: '—',
            notes: ['Add assets to compute the ratio.'],
          },
    'at-risk': {
      plainEnglish: 'Line items you rated “bad” or “critical”.',
      formulaHeadline: 'At risk = count(rating ∈ {bad, critical})',
      operands: [{ symbol: '#', label: 'Flagged items', value: String(atRiskCount) }],
      calculation: String(atRiskCount),
      result: items.length > 0 ? String(atRiskCount) : '—',
    },
    domains: {
      plainEnglish:
        domainFilter === 'all'
          ? 'Total registry rows split across personal and business tags.'
          : `Showing the ${domainFilter} domain filter only.`,
      formulaHeadline:
        domainFilter === 'all'
          ? 'Domains = personal items + business items'
          : 'Domains = active filter label',
      operands:
        domainFilter === 'all'
          ? [
              { symbol: 'P', label: 'Personal items', value: String(domainMix.personal) },
              { symbol: 'B', label: 'Business items', value: String(domainMix.business) },
            ]
          : [{ symbol: 'F', label: 'Active filter', value: domainFilter }],
      calculation:
        domainFilter === 'all'
          ? `${domainMix.personal} + ${domainMix.business}`
          : domainFilter,
      result:
        domainFilter !== 'all'
          ? domainFilter
          : domainMix.personal + domainMix.business > 0
            ? String(domainMix.personal + domainMix.business)
            : '—',
    },
  };
}

function companyEquityRows(companies: InvestmentCompany[]) {
  return companies.map((c) => ({
    label: c.name,
    value: computeCompanyMetrics(c).myEquityValue,
  }));
}

function companyTicketRows(companies: InvestmentCompany[]) {
  return companies.map((c) => ({
    label: c.name,
    value: typeof c.ticketSize === 'number' && Number.isFinite(c.ticketSize) ? c.ticketSize : 0,
  }));
}

export function buildInvestmentsKpiExplains(input: {
  companies: InvestmentCompany[];
  ticketTotal: number;
  equityTotal: number;
  valuationTotal: number;
  activeCount: number;
  investmentSlotsTotal: number;
  overallGain: number;
  portfolioValue: number;
  portfolioMoic: number | null;
  topHolding: { name: string; share: number; value: number } | null;
  weightedExitOdds: number | null;
  marketCount: number;
}): Record<string, KpiExplain> {
  const {
    companies,
    ticketTotal,
    activeCount,
    investmentSlotsTotal,
    overallGain,
    portfolioValue,
    portfolioMoic,
    topHolding,
    weightedExitOdds,
    marketCount,
  } = input;

  const valuationRows = companies.map((c) => ({
    label: c.name,
    value: computeCompanyMetrics(c).companyValuation,
  }));

  const expectedRows = companies.map((c) => ({
    label: c.name,
    value: computeCompanyMetrics(c).expectedReturnThisYear,
  }));

  const activeCompanies = companies.filter((c) => {
    const st = c.investmentStatus;
    return st !== 'exit' && st !== 'dead';
  });

  let weight = 0;
  const exitOddRows: { label: string; value: number }[] = [];
  for (const c of activeCompanies) {
    const equity = computeCompanyMetrics(c).myEquityValue;
    const prob = Number.isFinite(c.exitProbabilityThisYear) ? c.exitProbabilityThisYear : 0;
    weight += equity;
    exitOddRows.push({ label: c.name, value: prob });
  }

  return {
    equity: kpiSum(
      'Your paper stake in each company — company valuation times your ownership %.',
      'Equity total = Σ (company valuation × ownership %)',
      companyEquityRows(companies),
      formatEuro,
      'Valuation method per company: manual figure, revenue multiple, or EBITDA multiple.',
    ),
    investment: kpiSum(
      'Capital you deployed — the ticket size you recorded for each investment.',
      'Investment total = Σ ticketSize',
      companyTicketRows(companies),
      formatEuro,
    ),
    active: {
      plainEnglish: 'How many holdings are still marked active vs. exited or written off.',
      formulaHeadline: 'Active = count(status = active) / total holdings',
      operands: [
        { symbol: 'A', label: 'Active holdings', value: String(activeCount) },
        { symbol: 'T', label: 'Total holdings', value: String(investmentSlotsTotal) },
      ],
      calculation: `${activeCount} active of ${investmentSlotsTotal} total`,
      result: `${activeCount}/${investmentSlotsTotal}`,
    },
    valuation: kpiSum(
      'Headline enterprise values before your ownership slice.',
      'Valuation total = Σ companyValuation',
      valuationRows,
      formatEuro,
    ),
    overall: {
      plainEnglish: 'Paper profit or loss vs. what you paid in (ticket sizes).',
      formulaHeadline: 'Overall return = Portfolio value − Tickets paid',
      formulaDetail:
        'Portfolio value uses live equity marks for active names, exit proceeds when set, and zero for dead investments.',
      operands: [
        { symbol: 'PV', label: 'Portfolio value', value: formatEuro(portfolioValue) },
        { symbol: 'T', label: 'Tickets paid', value: formatEuro(ticketTotal) },
      ],
      calculation: `${formatEuro(portfolioValue)} − ${formatEuro(ticketTotal)}`,
      result: formatSignedEuro(overallGain),
    },
    moic:
      portfolioMoic != null
        ? kpiRatio(
            'Multiple on invested capital — how many times you have marked or realised vs. paid in.',
            'MOIC = Portfolio value ÷ Tickets paid',
            { label: 'Portfolio value', value: portfolioValue, formatted: formatEuro(portfolioValue) },
            { label: 'Tickets paid', value: ticketTotal, formatted: formatEuro(ticketTotal) },
            formatMultiple,
          )
        : {
            plainEnglish: 'Multiple on invested capital.',
            formulaHeadline: 'MOIC = Portfolio value ÷ Tickets paid',
            operands: [],
            calculation: '—',
            result: '—',
            notes: ['Enter ticket sizes to compute MOIC.'],
          },
    'top-holding': topHolding
      ? kpiRatio(
          'How much of the portfolio value sits in your largest stake.',
          'Top holding share = Largest stake ÷ Portfolio value × 100',
          { label: topHolding.name, value: topHolding.value, formatted: formatEuro(topHolding.value) },
          { label: 'Portfolio value', value: portfolioValue, formatted: formatEuro(portfolioValue) },
          (v) => formatPercentPoints(v * 100),
        )
      : {
          plainEnglish: 'Concentration in your single largest stake.',
          formulaHeadline: 'Top holding share = Largest stake ÷ Portfolio value × 100',
          operands: [],
          calculation: '—',
          result: '—',
        },
    'exit-odds':
      weightedExitOdds != null
        ? {
            plainEnglish: 'Average exit probability this year, weighted by equity value.',
            formulaHeadline: 'Avg. exit odds = Σ (exitProbability × equity) ÷ Σ equity',
            formulaDetail: 'Active holdings only; each company’s scenario probability is weighted by your equity mark.',
            operands: operandsFromItems(
              exitOddRows.map((r) => ({ label: `${r.label} (prob.)`, value: r.value })),
              (v) => formatPercentPoints(v),
            ),
            calculation:
              weight > 0
                ? `${activeCompanies
                    .map((c) => {
                      const eq = computeCompanyMetrics(c).myEquityValue;
                      const p = Number.isFinite(c.exitProbabilityThisYear)
                        ? c.exitProbabilityThisYear
                        : 0;
                      return `${formatPercentPoints(p)} × ${formatEuro(eq)}`;
                    })
                    .join(' + ')} ÷ ${formatEuro(weight)}`
                : '—',
            result: formatPercentPoints(weightedExitOdds),
          }
        : {
            plainEnglish: 'Equity-weighted average exit probability across active holdings.',
            formulaHeadline: 'Avg. exit odds = Σ (exitProbability × equity) ÷ Σ equity',
            operands: [],
            calculation: '—',
            result: '—',
          },
    markets: {
      plainEnglish: 'Distinct countries represented in your portfolio.',
      formulaHeadline: 'Markets = |unique countryCode|',
      operands: [{ symbol: '#', label: 'Unique country codes', value: String(marketCount) }],
      calculation: String(marketCount),
      result: marketCount > 0 ? String(marketCount) : '—',
      notes: ['Set a country flag on each holding to populate this metric.'],
    },
    expected: kpiSum(
      'Forward-looking scenario return: expected exit value plus modelled dividends this year.',
      'Expected return = Σ (equity × exitProbability + expectedDividend)',
      expectedRows,
      formatEuro,
      'Not guaranteed cash — a weighted scenario based on your inputs.',
    ),
  };
}

export function buildBankAccountsKpiExplains(
  accounts: BankAccount[],
  summary: {
    total: number;
    count: number;
    manualCount: number;
    connectedCount: number;
    largest: BankAccount | null;
    lastUpdated?: string;
  },
  formatSummaryDate: (iso?: string) => string,
): Record<string, KpiExplain> {
  const balanceRows = accounts.map((a) => ({
    label: a.name,
    value: typeof a.balance === 'number' && Number.isFinite(a.balance) ? a.balance : 0,
  }));

  return {
    'total-balance': kpiSum(
      'Combined spendable balance across all accounts on this view.',
      'Total balance = Σ account.balance',
      balanceRows,
      formatEuro,
      'Includes both connected (e.g. Qonto) and manually entered accounts.',
    ),
    accounts: {
      plainEnglish: 'How many bank accounts you track, split by connection type.',
      formulaHeadline: 'Accounts = connected + manual',
      operands: [
        { symbol: 'C', label: 'Connected accounts', value: String(summary.connectedCount) },
        { symbol: 'M', label: 'Manual accounts', value: String(summary.manualCount) },
      ],
      calculation: `${summary.connectedCount} + ${summary.manualCount}`,
      result: String(summary.count),
    },
    'largest-account': summary.largest
      ? {
          plainEnglish: 'The account with the highest current balance.',
          formulaHeadline: 'Largest account = max(account.balance)',
          operands: [
            {
              symbol: '★',
              label: summary.largest.name,
              value: formatEuro(summary.largest.balance ?? 0),
            },
          ],
          calculation: formatEuro(summary.largest.balance ?? 0),
          result: formatEuro(summary.largest.balance ?? 0),
        }
      : {
          plainEnglish: 'The account with the highest balance.',
          formulaHeadline: 'Largest account = max(account.balance)',
          operands: [],
          calculation: '—',
          result: '—',
        },
    'last-updated': {
      plainEnglish: 'When any account balance or bank connection last synced.',
      formulaHeadline: 'Last updated = max(balanceUpdatedAt, lastSyncAt)',
      operands: [
        {
          symbol: 'T',
          label: 'Most recent timestamp',
          value: summary.lastUpdated ? formatSummaryDate(summary.lastUpdated) : '—',
        },
      ],
      calculation: summary.lastUpdated ? formatSummaryDate(summary.lastUpdated) : '—',
      result: summary.lastUpdated ? formatSummaryDate(summary.lastUpdated) : '—',
    },
  };
}

export function buildRealEstateKpiExplains(
  properties: RealEstateProperty[],
  summary: {
    totalValue: number;
    equity: number;
    debt: number;
    monthlyRent: number;
    count: number;
  },
): Record<string, KpiExplain> {
  const valueRows = properties.map((p) => ({
    label: p.name,
    value: typeof p.currentValue === 'number' && Number.isFinite(p.currentValue) ? p.currentValue : 0,
  }));
  const mortgageRows = properties.map((p) => ({
    label: p.name,
    value:
      typeof p.mortgageBalance === 'number' && Number.isFinite(p.mortgageBalance)
        ? p.mortgageBalance
        : 0,
  }));
  const rentRows = properties.map((p) => ({
    label: p.name,
    value:
      typeof p.monthlyRent === 'number' && Number.isFinite(p.monthlyRent) ? p.monthlyRent : 0,
  }));
  const equityRows = properties.map((p) => {
    const value =
      typeof p.currentValue === 'number' && Number.isFinite(p.currentValue) ? p.currentValue : 0;
    const mortgage =
      typeof p.mortgageBalance === 'number' && Number.isFinite(p.mortgageBalance)
        ? p.mortgageBalance
        : 0;
    return { label: p.name, value: value - mortgage };
  });

  return {
    'net-equity': kpiSum(
      'What you truly own in property: market value minus remaining mortgage per building.',
      'Net equity = Σ (market value − mortgage balance)',
      equityRows,
      formatEuro,
    ),
    'total-value': kpiSum(
      'Combined market values before subtracting debt.',
      'Total value = Σ property.currentValue',
      valueRows,
      formatEuro,
    ),
    mortgages: kpiSum(
      'Outstanding loan balances secured against your properties.',
      'Mortgages = Σ property.mortgageBalance',
      mortgageRows,
      formatEuro,
    ),
    'monthly-rent': kpiSum(
      'Gross rental income you model across the portfolio.',
      'Monthly rent = Σ property.monthlyRent',
      rentRows,
      formatEuro,
      'Gross rent before expenses, vacancy, or management fees.',
    ),
  };
}

export function buildInvestmentCasesKpiExplains(
  cases: InvestmentCase[],
  summary: {
    total: number;
    investCount: number;
    avgScore: number;
    topCase: { name: string; score: number } | null;
    marketCount: number;
  },
): Record<string, KpiExplain> {
  const scoreRows = cases.map((c) => ({
    label: c.name,
    value: investmentCaseAverageScore(c),
  }));
  const scoreValues = scoreRows.map((r) => r.value);

  return {
    pipeline: {
      plainEnglish: 'How many investment cases you have scored in your dealflow.',
      formulaHeadline: 'Pipeline = count(investment cases)',
      operands: [{ symbol: '#', label: 'Cases in pipeline', value: String(summary.total) }],
      calculation: String(summary.total),
      result: String(summary.total),
    },
    'invest-ready': {
      plainEnglish: 'Cases whose average score clears the INVEST bar (average ≥ 4 on a 1–6 scale).',
      formulaHeadline: 'Invest-ready = count(average score ≥ 4)',
      operands: [
        { symbol: '✓', label: 'INVEST verdicts', value: String(summary.investCount) },
        { symbol: 'Σ', label: 'Total cases', value: String(summary.total) },
      ],
      calculation: `${summary.investCount} of ${summary.total} cases`,
      result: `${summary.investCount}/${summary.total}`,
      notes: ['Verdict uses the mean of five 1–6 ratings per case.'],
    },
    'avg-score': {
      plainEnglish: 'The mean score across every case — each case is the average of five dimensions.',
      formulaHeadline: 'Average score = Σ case averages ÷ number of cases',
      formulaDetail:
        'Per case: (founderAmbition + timeToRevenue + exitExperience + founderSkillset + marketSize) ÷ 5.',
      operands: operandsFromItems(
        scoreRows.map((r) => ({ label: r.label, value: r.value })),
        (v) => v.toFixed(2),
      ),
      calculation:
        summary.total > 0
          ? `${sumCalculation(scoreValues, (v) => v.toFixed(2))} ÷ ${summary.total}`
          : '—',
      result: summary.total > 0 ? summary.avgScore.toFixed(2) : '—',
    },
    'top-case': summary.topCase
      ? {
          plainEnglish: 'The highest-scoring opportunity in your pipeline right now.',
          formulaHeadline: 'Top case = max(case average score)',
          operands: [
            {
              symbol: '★',
              label: summary.topCase.name,
              value: summary.topCase.score.toFixed(2),
            },
          ],
          calculation: summary.topCase.score.toFixed(2),
          result: summary.topCase.score.toFixed(2),
        }
      : {
          plainEnglish: 'The highest-scoring case in the pipeline.',
          formulaHeadline: 'Top case = max(case average score)',
          operands: [],
          calculation: '—',
          result: '—',
        },
    markets: {
      plainEnglish: 'Distinct countries represented across your case pipeline.',
      formulaHeadline: 'Markets = |unique countryCode|',
      operands: [{ symbol: '#', label: 'Unique country codes', value: String(summary.marketCount) }],
      calculation: String(summary.marketCount),
      result: summary.marketCount > 0 ? String(summary.marketCount) : '—',
    },
  };
}
